import { useEffect, useRef, useState } from 'react'
import {
  clampSeekTime,
  formatMediaTime,
  fractionToTime,
  isSeekableDuration,
  seekStepSeconds,
  useVideoProgress,
} from '../../hooks/useVideoProgress'
import { useI18n } from '../../i18n'

interface VideoProgressBarProps {
  video: HTMLVideoElement | null
  /** Accessible name. */
  label?: string
  /** Told when a pointer drag starts and ends (the owner pins its controls meanwhile). */
  onScrubbingChange?: (scrubbing: boolean) => void
}

interface ScrubState {
  pointerId: number
  time: number
}

/** Keeps ARIA values stable and readable without rounding sub-second clips to nothing. */
function round1(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Swallows the `click` the browser dispatches after a drag that ended off the
 * bar.
 *
 * When `mousedown` and `mouseup` have different targets the click is dispatched
 * at their nearest common ancestor — the video surface, the stories stage, the
 * deck — which the bar's own `onClick` never sees. That click is dispatched
 * synchronously in the same task as `pointerup`, so a one-shot capture listener
 * disarmed on the next macrotask catches exactly it and nothing else. A
 * microtask would be too early: the microtask checkpoint runs between the
 * `pointerup` listeners and the click dispatch.
 */
function swallowClickAfterDrag() {
  const swallow = (ev: Event) => { ev.stopPropagation() }
  window.addEventListener('click', swallow, { capture: true, once: true })
  setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0)
}

/**
 * A scrubbable progress bar for one `<video>`.
 *
 * Seeking never changes play/pause state and never touches
 * `videoPlaybackStore`: the store stays the single owner of `activeVideoId`,
 * HLS load state and the manual-pause flags.
 */
export function VideoProgressBar({ video, label, onScrubbingChange }: VideoProgressBarProps) {
  const { t } = useI18n()
  const { currentTime, duration, seekTo } = useVideoProgress(video)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [scrub, setScrub] = useState<ScrubState | null>(null)
  // Mirror of `scrub` for the window listeners, which close over the state
  // value from the render that started the drag.
  const scrubRef = useRef<ScrubState | null>(null)
  const listenersRef = useRef<(() => void) | null>(null)

  // Unmounting mid-drag must not leave listeners on `window`.
  useEffect(() => () => { listenersRef.current?.() }, [])

  const timeFromClientX = (clientX: number): number => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (!rect || rect.width <= 0) return 0
    return fractionToTime((clientX - rect.left) / rect.width, duration)
  }

  const handlePointerDown = (e: PointerEvent) => {
    // `.card-media-video` toggles play on click, the VerticalStories stage and
    // the PlayerDeck container advance on click: none of them may see this.
    e.stopPropagation()
    // Primary button or touch/pen only. No preventDefault: it would suppress
    // the compat `mousedown` and with it focus. Text selection is blocked by
    // `user-select: none`, touch scrolling by `touch-action: none`.
    if (e.button !== undefined && e.button !== 0) return

    // Never stack two drags: a second touch replaces the one in flight. This
    // has to happen before the new pointer is registered below, because the
    // previous drag's `detach` clears `scrubRef` — running it afterwards would
    // wipe the incoming pointer's state and leave every later move and end
    // failing their pointer-id guard, with the window listeners still attached.
    listenersRef.current?.()

    const time = timeFromClientX(e.clientX)
    seekTo(time)
    const started: ScrubState = { pointerId: e.pointerId, time }
    scrubRef.current = started
    setScrub(started)
    onScrubbingChange?.(true)

    const onMove = (ev: PointerEvent) => {
      if (scrubRef.current?.pointerId !== ev.pointerId) return
      // The button was released outside the window: no `pointerup` is coming,
      // so end the drag here rather than seeking with the button up.
      if (ev.pointerType === 'mouse' && ev.buttons === 0) {
        detach()
        return
      }
      const next = timeFromClientX(ev.clientX)
      scrubRef.current = { pointerId: ev.pointerId, time: next }
      setScrub(scrubRef.current)
      seekTo(next)
    }
    const onEnd = (ev: PointerEvent) => {
      if (scrubRef.current?.pointerId !== ev.pointerId) return
      if (ev.type === 'pointerup') {
        seekTo(timeFromClientX(ev.clientX))
        // A release on the bar itself yields a click targeted at the bar, which
        // `onClick={stopPropagation}` already handles — and which capture-phase
        // ancestors (Studio's inspect-mode click-to-edit) must still see. Only a
        // release elsewhere produces the common-ancestor click the bar cannot
        // otherwise intercept. `composedPath()` rather than `contains(target)`:
        // this window listener sees the retargeted host from inside the open
        // shadow root. No click follows a `pointercancel`, so this is never
        // armed for one.
        const root = rootRef.current
        if (!root || !ev.composedPath().includes(root)) swallowClickAfterDrag()
      }
      detach()
    }
    const detach = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
      listenersRef.current = null
      scrubRef.current = null
      setScrub(null)
      onScrubbingChange?.(false)
    }

    // Pointer events are `composed`, so window listeners see them from inside
    // the shadow root; only `clientX` is read, which makes retargeting
    // irrelevant.
    listenersRef.current = detach
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    const step = seekStepSeconds(duration)
    let next: number | null = null
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = currentTime + step
        break
      case 'ArrowLeft':
      case 'ArrowDown':
        next = currentTime - step
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = duration
        break
      default:
        // Enter/Space/Tab bubble unchanged, same as the mute button today.
        return
    }
    e.preventDefault()
    e.stopPropagation()
    seekTo(clampSeekTime(next, duration))
  }

  const stopPropagation = (e: Event) => e.stopPropagation()

  // Nothing to scrub before metadata (`NaN`) or on a live stream (`Infinity`).
  if (!isSeekableDuration(duration)) return null

  // While dragging, show the drag position rather than `currentTime`, which
  // would jitter from in-flight seeks.
  const shown = scrub ? scrub.time : currentTime
  const fraction = duration > 0 ? shown / duration : 0
  const percent = `${fraction * 100}%`

  return (
    <div
      ref={rootRef}
      className={`card-media-progress${scrub ? ' card-media-progress--scrubbing' : ''}`}
      role="slider"
      tabIndex={0}
      aria-label={label ?? t('media.seek')}
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={round1(duration)}
      aria-valuenow={round1(shown)}
      aria-valuetext={t('media.position', { current: formatMediaTime(shown), duration: formatMediaTime(duration) })}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onClick={stopPropagation}
    >
      <div className="card-media-progress-track">
        <div className="card-media-progress-fill" style={{ width: percent }} />
        <div className="card-media-progress-thumb" style={{ left: percent }} />
      </div>
    </div>
  )
}
